import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { QRScanner } from "@/components/QRScanner";
import { InstallPWA } from "@/components/InstallPWA";
import { EnablePush } from "@/components/EnablePush";
import { SignaturePad } from "@/components/SignaturePad";
import { NavAppPicker, launchOrPick, getNavPref, clearNavPref } from "@/components/NavAppPicker";
import { useLiveSync } from "@/hooks/useLiveSync";
import { DriverChat } from "@/components/DriverChat";
import { Play, CheckCircle2, Flag, QrCode, Clock, LogOut, Phone, Navigation, Wifi, WifiOff, PenLine, AlertTriangle, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, STATUS_FLOW, today } from "@/lib/constants";

const NEXT_LABEL = { planned: "Démarrer (en route)", en_route: "Prise en charge du patient", picked_up: "Patient déposé — Terminer" };
const NEXT_ICON = { planned: Play, en_route: Navigation, picked_up: CheckCircle2 };
const STEPS = [
  { k: "planned", l: "À démarrer" },
  { k: "en_route", l: "En route" },
  { k: "picked_up", l: "Pris en charge" },
  { k: "finished", l: "Déposé" },
];

const QKEY = "pacemia_driver_queue";
const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => localStorage.setItem(QKEY, JSON.stringify(q));

export default function DriverApp() {
  const { user, logout } = useAuth();
  const [rides, setRides] = useState([]);
  const [forgot, setForgot] = useState(null); // {ride, status}
  const [manualTime, setManualTime] = useState("");
  const [qrRide, setQrRide] = useState(null);
  const [signRide, setSignRide] = useState(null);
  const [signing, setSigning] = useState(false);
  const [navTarget, setNavTarget] = useState(null); // {address, lat, lon}
  const [delayRide, setDelayRide] = useState(null);
  const [delayMinutes, setDelayMinutes] = useState(10);
  const [delayReason, setDelayReason] = useState("");
  const [delaySubmitting, setDelaySubmitting] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(readQ().length);
  const lastLocate = useRef(null);

  const openNav = (address, lat, lon) => {
    launchOrPick({ address, lat, lon, openPicker: () => setNavTarget({ address, lat, lon }) });
  };

  // --- Point GPS du domicile : le chauffeur valide l'adresse depuis le pas de la porte ---
  const [pinRide, setPinRide] = useState(null);
  const [pinPos, setPinPos] = useState(null); // {lat, lon, accuracy}
  const [pinNote, setPinNote] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const openPin = (r) => {
    setPinRide(r);
    setPinNote(r.patient_access_note || "");
    setPinPos(null);
    if (!navigator.geolocation) { toast.error("GPS indisponible sur cet appareil"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setPinPos({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      () => toast.error("Position GPS refusée — autorisez la localisation"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };
  const savePin = async () => {
    if (!pinPos) return;
    setPinBusy(true);
    try {
      const { data } = await api.post(`/driver/rides/${pinRide.id}/home-pin`, {
        lat: pinPos.lat, lon: pinPos.lon, accuracy_m: pinPos.accuracy, access_note: pinNote,
      });
      toast.success(`Domicile de ${data.patient_name} enregistré${data.rides_updated ? ` · ${data.rides_updated} course(s) recalculée(s)` : ""}`);
      setPinRide(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setPinBusy(false); }
  };

  const submitDelay = async () => {
    if (!delayRide) return;
    setDelaySubmitting(true);
    try {
      await api.post(`/rides/${delayRide.id}/signal-delay`, { minutes: delayMinutes, reason: delayReason });
      toast.success(`Retard signalé (+${delayMinutes} min)`);
      setDelayRide(null); setDelayReason(""); setDelayMinutes(10);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setDelaySubmitting(false); }
  };

  const load = useCallback(
    () => api.get("/driver/rides", { params: { date: today() } }).then((r) => setRides(r.data)).catch(() => {}),
    []
  );

  // Géoloc à la demande : envoi immédiat du GPS brut (une seule lecture, pas de suivi).
  const sendPosition = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => api.post("/driver/location", { lat: pos.coords.latitude, lon: pos.coords.longitude }).catch(() => {}),
      () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  // Temps réel : le régulateur affecte/modifie -> le chauffeur recharge instantanément.
  // « locate_request » = le régulateur a cliqué Actualiser -> on répond avec le GPS.
  useLiveSync((evt) => {
    load();
    if (evt?.type === "locate_request") {
      lastLocate.current = evt.requested_at || String(Date.now());
      sendPosition();
    }
  });

  // À l'ouverture / retour au premier plan : vérification UNIQUE d'une demande de
  // position manquée (aucun polling périodique — économie batterie).
  useEffect(() => {
    const check = () => api.get("/tracking/refresh-needed").then(({ data }) => {
      if (data.requested_at && data.requested_at !== lastLocate.current) {
        lastLocate.current = data.requested_at;
        sendPosition();
      }
    }).catch(() => {});
    check();
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [sendPosition]);

  const enqueue = (item) => { const q = readQ(); q.push(item); writeQ(q); setPending(q.length); };

  const flushQueue = useCallback(async () => {
    const q = readQ();
    if (!q.length) return;
    const remaining = [];
    for (const item of q) {
      try {
        await api.put(`/rides/${item.rideId}/driver-status`, { status: item.status, forgot: item.forgot, manual_time: item.manual_time });
      } catch (e) { console.error("Sync course hors-ligne échouée", e); remaining.push(item); }
    }
    writeQ(remaining); setPending(remaining.length);
    if (remaining.length === 0) { toast.success("Synchronisation terminée"); load(); }
  }, [load]);

  useEffect(() => { load(); flushQueue(); }, [load, flushQueue]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flushQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [flushQueue]);

  // Send GPS while any ride is en_route (best-effort background)
  // ▲ Ancien polling 5s supprimé (coût batterie / bande passante).
  // Le GPS est envoyé UNIQUEMENT sur demande du régulateur (locate_request)
  // ou à chaque transition d'état (avancement de course), voir advance().

  const advance = async (ride) => {
    // anti-saut d'étape : on n'avance que d'un cran dans le flux
    const next = STATUS_FLOW[STATUS_FLOW.indexOf(ride.status) + 1];
    if (!next) return;
    setRides((rs) => rs.map((r) => (r.id === ride.id ? { ...r, status: next } : r))); // optimiste
    try {
      await api.put(`/rides/${ride.id}/driver-status`, { status: next, forgot: false });
      // Envoi d'un point GPS à chaque transition d'état pour rafraîchir la carte du régulateur
      // sans consommer de batterie en continu (économie ≈ 90 % vs polling 5s).
      sendPosition();
      toast.success(`Statut : ${STATUS_META[next].label}`);
      load();
    } catch (err) {
      if (!navigator.onLine) {
        enqueue({ rideId: ride.id, status: next, forgot: false, manual_time: null });
        toast.info("Hors-ligne : changement enregistré, synchro auto au retour du réseau");
      } else {
        toast.error(apiError(err)); load();
      }
    }
  };

  const submitForgot = async () => {
    const item = { rideId: forgot.ride.id, status: forgot.status, forgot: true, manual_time: manualTime };
    setRides((rs) => rs.map((r) => (r.id === item.rideId ? { ...r, status: item.status, needs_correction: true } : r)));
    setForgot(null); setManualTime("");
    try {
      await api.put(`/rides/${item.rideId}/driver-status`, { status: item.status, forgot: true, manual_time: item.manual_time });
      toast.success("Oubli signalé — course transmise au régulateur");
      load();
    } catch (err) {
      if (!navigator.onLine) { enqueue(item); toast.info("Hors-ligne : oubli enregistré, synchro auto"); }
      else { toast.error(apiError(err)); load(); }
    }
  };

  const onQr = async (content) => {
    try {
      await api.post(`/rides/${qrRide.id}/pmti`, { content });
      toast.success("PMTi scannée et rattachée à la course");
      setQrRide(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const onSign = async (dataUrl) => {
    setSigning(true);
    try {
      await api.post(`/rides/${signRide.id}/signature`, {
        image_base64: dataUrl,
        signer_name: signRide.patient_name,
      });
      toast.success("Signature PMTi enregistrée");
      setSignRide(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSigning(false); }
  };

  const doLogout = async () => { await logout(); window.location.href = "/login"; };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 bg-card border-b border-border px-4 h-14 flex items-center justify-between">
        <Logo size={24} />
        <div className="flex items-center gap-2">
          {online ? (
            <Badge variant="outline" className="border-green-500 text-green-600 gap-1 text-[11px]" data-testid="driver-online">
              <Wifi className="h-3 w-3" /> En ligne
            </Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500 text-amber-600 gap-1 text-[11px]" data-testid="driver-offline">
              <WifiOff className="h-3 w-3" /> Hors-ligne
            </Badge>
          )}
          {pending > 0 && (
            <Badge variant="secondary" className="text-[11px]" data-testid="driver-pending">{pending} à synchroniser</Badge>
          )}
          <DriverChat rides={rides} />
          <InstallPWA />
          <EnablePush />
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={doLogout} data-testid="driver-logout"><LogOut className="h-5 w-5" /></Button>
        </div>
      </header>

      <div className="max-w-md mx-auto p-4 space-y-3">
        <div>
          <h1 className="font-display text-xl font-bold">Bonjour {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground" data-testid="driver-day-count">
            {rides.length === 0
              ? "0 course aujourd'hui"
              : (() => {
                  const total = rides.length;
                  const retours = rides.filter((r) => r.care_end).length;
                  const allers = total - retours;
                  const groupes = rides.filter((r) => r.shared).length;
                  const parts = [`${total} course${total > 1 ? "s" : ""} aujourd'hui`];
                  if (allers && retours) parts.push(`${allers} aller${allers > 1 ? "s" : ""} · ${retours} retour${retours > 1 ? "s" : ""}`);
                  if (groupes) parts.push(`${groupes} groupée${groupes > 1 ? "s" : ""}`);
                  return parts.join(" · ");
                })()}
          </p>
        </div>

        {rides.length === 0 && <Card className="p-8 rounded-md shadow-none text-center text-muted-foreground">Aucune course assignée aujourd'hui.</Card>}

        {rides.map((r) => {
          const Icon = NEXT_ICON[r.status];
          const done = r.status === "finished";
          return (
            <Card key={r.id} className="p-4 rounded-md shadow-none" data-testid={`driver-ride-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-bold text-lg tabular-nums" data-testid={`driver-ride-times-${r.id}`}>
                  {r.pec_time ? (<><span className="text-primary">{r.pec_time}</span><span className="opacity-60 mx-1">→</span></>) : null}
                  {r.time}
                </span>
                <Badge className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label}</Badge>
              </div>
              {(r.shared || r.care_end) && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {r.care_end && <Badge variant="outline" className="text-[10px] border-amber-400 bg-amber-50 text-amber-800">Retour · fin de soin {r.care_end}</Badge>}
                  {r.shared && <Badge variant="outline" className="text-[10px] border-primary/40 bg-primary/5 text-primary">Groupée ×{r.group_size}</Badge>}
                </div>
              )}
              {r.driver_start_time && (
                <div className={`mb-2 rounded-md px-2.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 ${r.early_departure ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid={`driver-depart-${r.id}`}>
                  {r.early_departure ? "⚠️" : "🚦"} Départ société à {r.driver_start_time}{r.approach_min ? ` · ${r.approach_min} min de route jusqu'au patient` : ""}{r.early_departure ? " — avant le début de service" : ""}
                </div>
              )}
              {r.delayed && (
                <div className="mb-2 rounded-md px-2.5 py-1.5 text-sm font-semibold bg-red-100 text-red-800 flex items-start gap-1.5" data-testid={`driver-delay-${r.id}`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    En retard de {r.delay_min} min
                    {r.delay_source === "driver" ? " (signalé)" : " (auto)"}
                    {r.delay_reason ? ` · « ${r.delay_reason} »` : ""}
                  </span>
                </div>
              )}
              {r.needs_reassign && !r.delayed && (
                <div className="mb-2 rounded-md px-2.5 py-1.5 text-sm font-semibold bg-orange-100 text-orange-800" data-testid={`driver-reassign-${r.id}`}>
                  ⚠️ Décalage possible (+{r.cascade_delay_min || 0} min)
                  {r.expected_pickup_time ? ` — arrivée estimée ${r.expected_pickup_time}` : ""}
                </div>
              )}
              <p className="font-medium">{r.patient_name}</p>
              {r.patient_phone && <a href={`tel:${r.patient_phone}`} className="text-sm text-primary flex items-center gap-1"><Phone className="h-3 w-3" />{r.patient_phone}</a>}
              <button type="button" onClick={() => openNav(r.pickup_address, r.pickup_lat, r.pickup_lon)}
                 className="mt-1.5 flex items-start gap-1.5 text-xs text-primary hover:underline active:opacity-70 w-full text-left" data-testid={`driver-nav-pickup-${r.id}`}>
                <Navigation className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><span className="text-muted-foreground">Prise en charge · </span>{r.pickup_address}</span>
              </button>
              <button type="button" onClick={() => openNav(r.dropoff_address, r.dropoff_lat, r.dropoff_lon)}
                 className="mt-1 flex items-start gap-1.5 text-xs text-primary hover:underline active:opacity-70 w-full text-left" data-testid={`driver-nav-dropoff-${r.id}`}>
                <Navigation className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><span className="text-muted-foreground">Dépose · </span>{r.dropoff_address}</span>
              </button>
              {r.needs_correction && <Badge variant="destructive" className="mt-2">En attente correction régulateur</Badge>}
              {r.pmti_scanned && <Badge variant="outline" className="mt-2 border-green-500 text-green-600">PMTi scannée</Badge>}
              {r.signature_status === "signed" && <Badge variant="outline" className="mt-2 ml-2 border-green-500 text-green-600 gap-1"><PenLine className="h-3 w-3" /> PMTi signée</Badge>}

              {r.driver_end_time && (
                <div className={`mt-2 rounded-md px-2.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 ${r.over_amplitude ? "bg-red-100 text-red-800" : r.late_return ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`driver-return-${r.id}`}>
                  {r.over_amplitude ? "⛔" : r.late_return ? "⚠️" : "🏁"}
                  <span>
                    Retour société à {r.driver_end_time}
                    {r.return_min ? ` · ${r.return_min} min` : ""}
                    {r.amplitude_min ? ` · amplitude ${Math.floor(r.amplitude_min/60)}h${String(r.amplitude_min%60).padStart(2,"0")}` : ""}
                    {r.over_amplitude && " — dépassement maximum"}
                    {!r.over_amplitude && r.late_return && " — après la fin de service"}
                  </span>
                </div>
              )}

              {r.patient_access_note && (
                <div className="mt-2 rounded-md px-2.5 py-1.5 text-xs bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200" data-testid={`driver-access-note-${r.id}`}>
                  <span className="font-semibold">Accès : </span>{r.patient_access_note}
                </div>
              )}
              {r.patient_id && (
                <div className="mt-2">
                  {r.patient_home_pin ? (
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <Badge variant="outline" className="border-emerald-500 text-emerald-600 gap-1" data-testid={`driver-pin-ok-${r.id}`}>
                        <MapPin className="h-3 w-3" /> Domicile pointé GPS
                      </Badge>
                      <span className="text-muted-foreground">
                        par {r.patient_home_pin.by_name || "un chauffeur"} le {new Date(r.patient_home_pin.at).toLocaleDateString("fr-FR")}
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => openPin(r)} data-testid={`driver-pin-edit-${r.id}`}>
                        Corriger
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-md px-2.5 py-2 bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200" data-testid={`driver-pin-missing-${r.id}`}>
                      <p className="text-xs font-semibold">Adresse jamais confirmée sur place</p>
                      <Button size="sm" variant="outline" className="mt-1.5 h-8 gap-1.5 text-xs bg-background" onClick={() => openPin(r)} data-testid={`driver-pin-add-${r.id}`}>
                        <MapPin className="h-3.5 w-3.5" /> Enregistrer le point GPS du domicile
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex items-center gap-1" data-testid={`driver-steps-${r.id}`}>
                {STEPS.map((s, i) => {
                  const cur = STATUS_FLOW.indexOf(r.status);
                  const done = i <= cur;
                  return (
                    <div key={s.k} className="flex-1">
                      <div className={`h-1.5 rounded-full transition-colors ${done ? "bg-primary" : "bg-secondary"}`} />
                      <p className={`mt-1 text-[9px] text-center leading-tight ${i === cur ? "font-bold text-primary" : "text-muted-foreground"}`}>{s.l}</p>
                    </div>
                  );
                })}
              </div>

              {!done ? (
                <Button className="w-full mt-2 h-12 text-base font-semibold gap-2" onClick={() => advance(r)} data-testid={`driver-action-${r.id}`}>
                  <Icon className="h-5 w-5" /> {NEXT_LABEL[r.status]}
                </Button>
              ) : (
                <Button disabled className="w-full mt-2 h-12 text-base font-semibold gap-2 bg-emerald-600 disabled:opacity-100 text-white" data-testid={`driver-done-${r.id}`}>
                  <CheckCircle2 className="h-5 w-5" /> Course terminée
                </Button>
              )}

              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1 text-xs" onClick={() => { setForgot({ ride: r, status: STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(r.status) + 1, 3)] }); }} data-testid={`driver-forgot-${r.id}`}>
                  <Flag className="h-3 w-3" /> Oubli d'horodatage
                </Button>
                <Button variant="outline" size="sm" className="flex-1 gap-1 text-xs" onClick={() => setQrRide(r)} data-testid={`driver-qr-${r.id}`}>
                  <QrCode className="h-3 w-3" /> Scanner PMTi
                </Button>
              </div>
              {!done && (
                <Button variant="outline" size="sm" className="w-full gap-1 text-xs mt-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                  onClick={() => setDelayRide(r)} data-testid={`driver-signal-delay-${r.id}`}>
                  <AlertTriangle className="h-3 w-3" /> Je serai en retard
                </Button>
              )}
              <Button variant="outline" size="sm" className="w-full gap-1 text-xs mt-2" onClick={() => setSignRide(r)} data-testid={`driver-sign-${r.id}`}>
                <PenLine className="h-3 w-3" /> {r.signature_status === "signed" ? "Re-signer PMTi" : "Faire signer le patient"}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Forgot timestamp dialog */}
      <Dialog open={!!forgot} onOpenChange={(o) => !o && setForgot(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Signaler un oubli</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Saisissez l'heure approximative de mémoire. La course sera transmise au régulateur pour validation.</p>
          <div>
            <Label>Heure approximative</Label>
            <Input type="time" value={manualTime} onChange={(e) => setManualTime(e.target.value)} className="mt-1" data-testid="forgot-time" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgot(null)}>Annuler</Button>
            <Button onClick={submitForgot} disabled={!manualTime} data-testid="forgot-submit">Clôturer la course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signal delay dialog */}
      <Dialog open={!!delayRide} onOpenChange={(o) => !o && setDelayRide(null)}>
        <DialogContent className="max-w-sm" data-testid="delay-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Signaler un retard</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Prévenir le régulateur — il pourra ajuster les courses suivantes.</p>
          <div className="space-y-3">
            <div>
              <Label>Retard estimé</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {[10, 15, 30, 60].map(m => (
                  <Button key={m} type="button" size="sm"
                    variant={delayMinutes === m ? "default" : "outline"}
                    onClick={() => setDelayMinutes(m)}
                    data-testid={`delay-preset-${m}`}>
                    +{m} min
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Motif (facultatif)</Label>
              <Input value={delayReason} onChange={(e) => setDelayReason(e.target.value)}
                placeholder="Bouchons, patient pas prêt, attente radio…" data-testid="delay-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelayRide(null)}>Annuler</Button>
            <Button onClick={submitDelay} disabled={delaySubmitting} data-testid="delay-submit">
              {delaySubmitting ? "Envoi…" : `Signaler +${delayMinutes} min`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Point GPS du domicile patient */}
      <Dialog open={!!pinRide} onOpenChange={(o) => !o && setPinRide(null)}>
        <DialogContent className="max-w-sm" data-testid="pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Point GPS du domicile</DialogTitle>
            <DialogDescription className="text-xs">
              Placez-vous devant la porte du patient. Ce point deviendra la référence pour la navigation
              et les distances — l'adresse écrite reste inchangée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-secondary px-3 py-2 text-sm" data-testid="pin-position">
              {pinPos ? (
                <>
                  <p className="font-semibold">{pinRide?.patient_name}</p>
                  <p className="tabular-nums">{pinPos.lat.toFixed(5)} · {pinPos.lon.toFixed(5)}</p>
                  <p className={`text-xs ${pinPos.accuracy > 50 ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>
                    Précision ± {pinPos.accuracy} m{pinPos.accuracy > 50 ? " — approchez-vous ou réessayez" : ""}
                  </p>
                </>
              ) : (
                <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Acquisition du GPS…</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Consigne d'accès (visible par les chauffeurs suivants)</Label>
              <Input value={pinNote} onChange={(e) => setPinNote(e.target.value)} className="mt-1"
                placeholder="Portail bleu au fond du chemin, sonner chez le voisin…" data-testid="pin-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinRide(null)}>Annuler</Button>
            <Button variant="secondary" onClick={() => openPin(pinRide)} disabled={pinBusy} data-testid="pin-retry">Relever à nouveau</Button>
            <Button onClick={savePin} disabled={!pinPos || pinBusy} data-testid="pin-save">
              {pinBusy ? "Enregistrement…" : "Enregistrer ce point"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR scanner dialog */}
      <Dialog open={!!qrRide} onOpenChange={(o) => !o && setQrRide(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5" /> Scan PMTi</DialogTitle></DialogHeader>
          {qrRide && <QRScanner onResult={onQr} onClose={() => setQrRide(null)} />}
        </DialogContent>
      </Dialog>

      {/* Signature dialog */}
      <Dialog open={!!signRide} onOpenChange={(o) => !o && setSignRide(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="h-5 w-5" /> Signature PMTi</DialogTitle></DialogHeader>
          {signRide && (
            <>
              <DialogDescription className="text-sm text-muted-foreground">Faites signer <span className="font-medium text-foreground">{signRide.patient_name}</span> dans le cadre ci-dessous. La signature est horodatée et archivée (valeur médico-légale).</DialogDescription>
              <SignaturePad onSave={onSign} saving={signing} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Navigation app picker */}
      <NavAppPicker
        open={!!navTarget}
        onClose={() => setNavTarget(null)}
        address={navTarget?.address}
        lat={navTarget?.lat}
        lon={navTarget?.lon}
      />
      {getNavPref() && (
        <div className="fixed bottom-2 left-0 right-0 text-center text-[10px] text-muted-foreground pointer-events-none">
          Nav par défaut : {getNavPref()}
          <button type="button" onClick={() => { clearNavPref(); toast.success("Choix de navigation réinitialisé"); }}
                  className="ml-1 underline pointer-events-auto" data-testid="driver-nav-reset">
            réinitialiser
          </button>
        </div>
      )}
    </div>
  );
}

